// ============================================================================
// MÓDULO 7 - UNIDAD 7: CITIES & SEPARABLE PHRASAL VERBS
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Separable phrasal verbs (noun flexibility + PRONOUN RULE!)
// Vocabulario: City sights, adjectives, UK-US urban vocabulary
// Critical focus: Pronoun placement (pick IT up, NOT pick up it)
// ============================================================================

export const M7_UNIT_7 = {
  id: "m7-u7",
  title: "Unit 7: City Life & Phrasal Verbs",
  topic: "Master city vocabulary, describe urban life, and use separable phrasal verbs correctly - especially with pronouns!",
  
  grammarImage: "/images/grammar/m7-u7-separable-phrasal-verbs.jpg",
  grammarText: `
    <h3>Separable Phrasal Verbs - City Life</h3>
    
    <div class="grammar-rule">
      <h4>What are Separable Phrasal Verbs?</h4>
      <p>Verb + particle combinations where the object can move:</p>
      <ul>
        <li><strong>Pick up</strong> the litter</li>
        <li><strong>Turn on</strong> the light</li>
        <li><strong>Clean up</strong> the street</li>
      </ul>
    </div>

    <div class="noun-rule">
      <h4>Rule 1: Nouns (Flexible Position)</h4>
      <p>With NOUN objects, both positions work:</p>
      <ul>
        <li>✅ Pick up <strong>the litter</strong></li>
        <li>✅ Pick <strong>the litter</strong> up</li>
        <li>✅ Turn on <strong>the lights</strong></li>
        <li>✅ Turn <strong>the lights</strong> on</li>
      </ul>
    </div>

    <div class="pronoun-rule-critical">
      <h4>Rule 2: Pronouns (MUST Go in Middle!) ⚠️</h4>
      <p>With PRONOUN objects (it, them, him, her), ONLY middle position:</p>
      <ul>
        <li>✅ Pick <strong>it</strong> up (CORRECT!)</li>
        <li>❌ Pick up <strong>it</strong> (WRONG!)</li>
        <li>✅ Turn <strong>them</strong> on (CORRECT!)</li>
        <li>❌ Turn on <strong>them</strong> (WRONG!)</li>
      </ul>
      <p class="warning">This is one of the most common errors - even at advanced levels!</p>
    </div>

    <div class="common-phrasal-verbs">
      <h4>Common Separable Phrasal Verbs</h4>
      <ul>
        <li><strong>pick up</strong> - recoger</li>
        <li><strong>clean up</strong> - limpiar</li>
        <li><strong>throw away</strong> - tirar</li>
        <li><strong>show off</strong> - presumir/mostrar</li>
        <li><strong>point out</strong> - señalar</li>
        <li><strong>find out</strong> - descubrir</li>
        <li><strong>try out</strong> - probar</li>
        <li><strong>turn on/off</strong> - encender/apagar</li>
      </ul>
    </div>
  `,

  iconType: "city",

  sets: [
    // ========================================================================
    // SET 1: CITY SIGHTS & LANDMARKS 🏙️
    // ========================================================================
    {
      id: "m7-u7-s1",
      title: "Set 1: City Sights & Landmarks 🏙️",
      context: `**Tourist Information Center**

Tourist: "Hi! I'm visiting for three days. What should I see?"

Guide: "Welcome! Our city has amazing landmarks..."`,
      
      questions: [
        {
          type: "match",
          q: "Match city landmarks with their descriptions:",
          pairs: [
            { left: "Skyscraper", right: "Very tall modern building" },
            { left: "Art gallery", right: "Place displaying paintings and art" },
            { left: "Monument", right: "Structure commemorating person/event" },
            { left: "Cathedral", right: "Large, important church" }
          ],
          a: "completed",
          successtip: "✓ CITY LANDMARKS!\nThese define a city's character. Skyscrapers = modern, cathedrals = historic! 🏙️⛪"
        },
        {
          type: "mc",
          q: "Guide: 'Our ___ is the tallest building in the city - 60 floors!'",
          options: [
            "skyscraper",
            "monument",
            "bridge"
          ],
          a: "skyscraper",
          successtip: "✓ TALL BUILDING!\n'Skyscraper' = very tall building. From 'scrapes the sky'! NYC, Dubai famous for them! 🏢☁️"
        },
        {
          type: "input",
          q: "Tourist: 'I love art! Is there an art ___ nearby?'",
          a: "gallery",
          accept: { ignoreCase: true, alts: ["museum"] },
          successtip: "✓ ART SPACE!\n'Art gallery' = displays art. 'Museum' = broader (history, science, art). Both valid! 🎨🖼️"
        },
        {
          type: "mc",
          q: "Guide: 'The central ___ is perfect for relaxing. Lots of green space!'",
          options: [
            "park",
            "parking",
            "pavement"
          ],
          a: "park",
          successtip: "✓ GREEN SPACE!\nUrban parks = essential! NYC's Central Park, London's Hyde Park = famous examples! 🌳🏙️"
        },
        {
          type: "order",
          q: "Traduce: La plaza principal tiene una fuente hermosa",
          a: "The main square has a beautiful fountain",
          successtip: "✓ CITY CENTER!\n'Square' or 'plaza' = central gathering place. Fountains = classic feature! ⛲"
        },
        {
          type: "mc",
          q: "In the UK, where you buy medicine is called a ___.",
          options: [
            "chemist's",
            "pharmacy",
            "drugstore"
          ],
          a: "chemist's",
          successtip: "✓ UK TERM!\nUK: 'chemist's' or 'chemist'. US: 'pharmacy' or 'drugstore'. Same place! 🇬🇧💊"
        },
        {
          type: "input",
          q: "US: 'subway' = UK: '___ ' or 'Tube' (in London)",
          a: "underground",
          accept: { ignoreCase: true, alts: ["the underground", "tube", "the tube"] },
          successtip: "✓ METRO SYSTEM!\nUK: 'Underground' (or 'Tube' in London). US: 'Subway'. Same transport! 🚇🇬🇧🇺🇸"
        },
        {
          type: "mc",
          q: "Guide: 'Our historic ___ crosses the river - it's 200 years old!'",
          options: [
            "bridge",
            "building",
            "tower"
          ],
          a: "bridge",
          successtip: "✓ RIVER CROSSING!\nFamous bridges: London Bridge, Brooklyn Bridge, Golden Gate! City icons! 🌉"
        },
        {
          type: "order",
          q: "Traduce: El museo está en el centro de la ciudad",
          a: "The museum is in the city center",
          successtip: "✓ LOCATION!\nUK: 'city centre'. US: 'downtown' or 'city center'. Both mean central area! 🏛️📍"
        },
        {
          type: "mc",
          q: "Tourist: 'Where's the nearest cinema?' (UK term) Guide: 'You mean the ___?' (US term)",
          options: [
            "movie theater",
            "film house",
            "picture palace"
          ],
          a: "movie theater",
          successtip: "✓ MOVIE PLACE!\nUK: 'cinema'. US: 'movie theater'. Same place for watching films! 🎬🇬🇧🇺🇸"
        }
      ]
    },

    // ========================================================================
    // SET 2: DESCRIBING CITIES - ADJECTIVES 🌆
    // ========================================================================
    {
      id: "m7-u7-s2",
      title: "Set 2: City Adjectives 🌆",
      context: `**Travel Blog - City Reviews**

"I've visited 50 cities worldwide. Let me describe them..."`,
      
      questions: [
        {
          type: "match",
          q: "Match adjectives with their meanings:",
          pairs: [
            { left: "Vibrant", right: "Full of life and energy" },
            { left: "Bustling", right: "Busy with activity" },
            { left: "Polluted", right: "Contaminated, dirty air" },
            { left: "Peaceful", right: "Calm and quiet" }
          ],
           a: "completed",
          successtip: "✓ CITY DESCRIPTIONS!\nVibrant/bustling = positive energy. Polluted = negative. Peaceful = relaxed! 🏙️"
        },
        {
          type: "mc",
          q: "Blog: 'Tokyo is incredibly ___ - there are people and activity everywhere!'",
          options: [
            "bustling",
            "peaceful",
            "empty"
          ],
          a: "bustling",
          successtip: "✓ BUSY CITY!\n'Bustling' = lots of activity, people, movement. Positive/neutral term! Tokyo perfect example! 🗾🏙️"
        },
        {
          type: "input",
          q: "Blog: 'Barcelona's nightlife is ___ - so much energy!' (full of life)",
          a: "vibrant",
          accept: { ignoreCase: true, alts: ["lively"] },
          successtip: "✓ ENERGETIC!\n'Vibrant' = lleno de vida. 'Lively' = similar. Both positive for cities with energy! ✨🎉"
        },
        {
          type: "mc",
          q: "Blog: 'Unfortunately, Beijing can be quite ___ - the air quality is poor.'",
          options: [
            "polluted",
            "clean",
            "fresh"
          ],
          a: "polluted",
          successtip: "✓ ENVIRONMENTAL ISSUE!\n'Polluted' = contaminated air/water. Major problem in big cities worldwide! 🏭😷"
        },
        {
          type: "order",
          q: "Traduce: Mi ciudad natal es tranquila y encantadora",
          a: "My hometown is peaceful and charming",
          successtip: "✓ HOMETOWN LOVE!\n'Peaceful' + 'charming' = great combo for small, nice cities! 🏡💚"
        },
        {
          type: "mc",
          q: "Blog: 'New York is ___ - it has people from every country!'",
          options: [
            "diverse",
            "uniform",
            "homogeneous"
          ],
          a: "diverse",
          successtip: "✓ MULTICULTURAL!\n'Diverse' = many different cultures, backgrounds. NYC, London, Toronto = very diverse! 🌍🤝"
        },
        {
          type: "input",
          q: "Blog: 'The subway was incredibly ___ during rush hour - barely any space!' (abarrotado)",
          a: "crowded",
          accept: { ignoreCase: true, alts: ["packed", "jam-packed"] },
          successtip: "✓ TOO MANY PEOPLE!\n'Crowded' = lleno de gente. Rush hour in any big city = nightmare! 🚇😰"
        },
        {
          type: "mc",
          q: "Blog: 'Dubai is ultra-___ - everything is new and high-tech!'",
          options: [
            "modern",
            "ancient",
            "historic"
          ],
          a: "modern",
          successtip: "✓ NEW & TECH!\n'Modern' = contemporary, new. Opposite: 'historic/ancient'. Dubai = extremely modern! 🇦🇪✨"
        },
        {
          type: "order",
          q: "Traduce: Roma es una ciudad histórica con monumentos antiguos",
          a: "Rome is a historic city with ancient monuments",
          successtip: "✓ ANCIENT CAPITAL!\n'Historic' = important in history. 'Ancient' = very old. Rome = both! 🇮🇹🏛️"
        },
        {
          type: "mc",
          q: "Blog: 'The old town was unfortunately quite ___ - the buildings need renovation.'",
          options: [
            "run-down",
            "modern",
            "vibrant"
          ],
          a: "run-down",
          successtip: "✓ NEEDS REPAIR!\n'Run-down' = deteriorated, needs fixing. Sad but common in old areas! 🏚️😔"
        }
      ]
    },

    // ========================================================================
    // SET 3: YOUR CITY DESCRIPTION 🗺️
    // ========================================================================
    {
      id: "m7-u7-s3",
      title: "Set 3: Describing Your City 🗺️",
      context: `**Making New Friends Online**

New friend: "Tell me about your city! What's it like?"

You: "Well, let me describe it..."`,
      
      questions: [
        {
          type: "mc",
          q: "You: 'My city is quite ___ - there's always something happening!'",
          options: [
            "lively",
            "boring",
            "dead"
          ],
          a: "lively",
          successtip: "✓ ACTIVE CITY!\n'Lively' = animated, lots happening. Great for social people! Similar: vibrant, bustling! 🎉"
        },
        {
          type: "input",
          q: "Friend: 'Is it ___ to walk around?' You: 'Oh yes, very low crime.' (safe)",
          a: "safe",
          accept: { ignoreCase: true, alts: ["secure"] },
          successtip: "✓ SAFETY MATTERS!\n'Safe' vs 'dangerous' = major factor when choosing where to live! 🔒✅"
        },
        {
          type: "mc",
          q: "You: 'The downtown area is very ___ - so many cars and people!'",
          options: [
            "noisy",
            "quiet",
            "silent"
          ],
          a: "noisy",
          successtip: "✓ SOUND POLLUTION!\n'Noisy' = lots of sound. Downtown = usually noisy. Suburbs = quieter! 🔊🚗"
        },
        {
          type: "order",
          q: "Traduce: Hay muchos rascacielos en el distrito financiero",
          a: "There are many skyscrapers in the financial district",
          successtip: "✓ BUSINESS AREA!\n'Financial district' = business zone. Usually has tallest buildings! Wall Street, Canary Wharf! 💼🏢"
        },
        {
          type: "mc",
          q: "You: 'We have a beautiful ___ where people gather on weekends.' (central open space)",
          options: [
            "square / plaza",
            "skyscraper",
            "tunnel"
          ],
          a: "square / plaza",
          successtip: "✓ GATHERING PLACE!\nPlazas/squares = heart of cities. Events, markets, protests happen here! Historical centers! 🏛️👥"
        },
        {
          type: "input",
          q: "UK: 'pavement' = US: '___' (where pedestrians walk)",
          a: "sidewalk",
          accept: { ignoreCase: true },
          successtip: "✓ WALKING PATH!\nUK: 'pavement'. US: 'sidewalk'. Same pedestrian walkway! Confusing: US 'pavement' = road surface! 🚶🇬🇧🇺🇸"
        },
        {
          type: "mc",
          q: "Friend: 'How's public transport?' You: 'Great! We have buses and a ___.' (underground train)",
          options: [
            "subway / metro",
            "taxi",
            "bicycle"
          ],
          a: "subway / metro",
          successtip: "✓ UNDERGROUND!\nMany names: subway (US), metro (Europe), underground/tube (UK), MRT (Asia)! Same thing! 🚇🌍"
        },
        {
          type: "order",
          q: "Traduce: La galería de arte está cerca de la estación de metro",
          a: "The art gallery is near the metro station",
          successtip: "✓ CONVENIENT LOCATION!\nCultural sites near transport = perfect! 'Metro' = universal term now! 🎨🚇"
        },
        {
          type: "mc",
          q: "You: 'The city center can get very ___ during peak hours - too many tourists!'",
          options: [
            "crowded",
            "empty",
            "deserted"
          ],
          a: "crowded",
          successtip: "✓ TOURIST SEASON!\n'Crowded' = packed with people. Peak season in popular cities = nightmare! 😅👥"
        },
        {
          type: "mc",
          q: "Friend: 'What do you love most?' You: 'The ___ culture - so many different communities!'",
          options: [
            "diverse",
            "uniform",
            "single"
          ],
          a: "diverse",
          successtip: "✓ CULTURAL MIX!\nDiversity = city strength! Different foods, languages, traditions = richer experience! 🌍🤝💚"
        }
      ]
    },

    // ========================================================================
    // SET 4: NOUN OBJECTS - FLEXIBLE POSITION 📦
    // ========================================================================
    {
      id: "m7-u7-s4",
      title: "Set 4: Phrasal Verbs - Nouns (Flexible!) 📦",
      context: `**City Cleanup Campaign**

Organizer: "Thanks for volunteering! Let me explain what we're doing today..."`,
      
      questions: [
        {
          type: "mc",
          q: "Organizer: 'First, please pick up the litter.' This means the same as:",
          options: [
            "Pick the litter up (both correct!)",
            "Only one way is correct",
            "Neither is correct"
          ],
          a: "Pick the litter up (both correct!)",
          successtip: "✓ BOTH WAYS!\nWith NOUNS, both positions work! 'Pick up the litter' = 'Pick the litter up'. Flexible! 📦✅"
        },
        {
          type: "input",
          q: "Organizer: 'Clean ___ the streets' or 'Clean the streets ___' - both work!",
          a: "up, up",
          accept: { ignoreCase: true, alts: ["up / up"] },
          successtip: "✓ FLEXIBLE POSITION!\n'Clean up streets' OR 'Clean streets up' = both fine with nouns! 🧹"
        },
        {
          type: "mc",
          q: "Volunteer: 'Should I throw away the trash?' = 'Should I throw ______?'",
          options: [
            "the trash away (both work!)",
            "Only 'throw away the trash' is correct",
            "Neither works"
          ],
          a: "the trash away (both work!)",
          successtip: "✓ NOUN FLEXIBILITY!\n'Throw away trash' = 'Throw trash away'. With nouns = your choice! 🗑️"
        },
        {
          type: "order",
          q: "Traduce: Vamos a recoger la basura del parque (use 'pick up')",
          a: "We are going to pick up the litter from the park",
          successtip: "✓ CLEANUP ACTION!\nCould also say 'pick the litter up'! Both correct with noun 'litter'! 🧹🌳"
        },
        {
          type: "mc",
          q: "Organizer: 'Please turn on the lights' can also be said as:",
          options: [
            "'Turn the lights on'",
            "Only one way is correct",
            "Can't be changed"
          ],
          a: "'Turn the lights on'",
          successtip: "✓ LIGHTING UP!\n'Turn on lights' = 'Turn lights on'. Noun 'lights' = flexible position! 💡🔄"
        },
        {
          type: "mc",
          q: "Which is TRUE about separable phrasal verbs with NOUNS?",
          options: [
            "The noun can go before OR after the particle",
            "The noun must always go after the particle",
            "The noun must always go before the particle"
          ],
          a: "The noun can go before OR after the particle",
          successtip: "✓ NOUN RULE!\nNouns = flexible! Can go either position. Your choice! This changes with pronouns... 🔄"
        },
        {
          type: "order",
          q: "Rewrite: 'Pick up the trash' → 'Pick ___' (move noun)",
          a: "the trash up",
          successtip: "✓ POSITION SWITCH!\nMoved noun to middle! Both versions equally correct! Natural English! 🔄✅"
        },
        {
          type: "mc",
          q: "Organizer: 'Fill out the form' = 'Fill ___ ___' (move the noun)",
          options: [
            "the form out",
            "out form the",
            "form the out"
          ],
          a: "the form out",
          successtip: "✓ PAPERWORK!\n'Fill out form' = 'Fill form out'. Both natural with noun! 📝✅"
        },
        {
          type: "input",
          q: "'Clean up the area' can be rewritten as 'Clean ___ ___ ___.'",
          a: "the area up",
          accept: { ignoreCase: true },
          successtip: "✓ ALTERNATIVE POSITION!\nSame meaning, different word order! Both sound natural! 🧹🔄"
        },
        {
          type: "mc",
          q: "Summary: With NOUN objects, separable phrasal verbs are:",
          options: [
            "Flexible - noun can go before or after particle",
            "Fixed - only one position works",
            "Random - no rules"
          ],
          a: "Flexible - noun can go before or after particle",
          successtip: "✓ NOUN FLEXIBILITY MASTERED!\nRemember: This changes with pronouns! Next set = critical rule! ⚠️"
        }
      ]
    },

    // ========================================================================
    // SET 5: COMMON SEPARABLE PHRASAL VERBS 🔄
    // ========================================================================
    {
      id: "m7-u7-s5",
      title: "Set 5: Common City Phrasal Verbs 🔄",
      context: `**Daily City Life**

Let's learn the most common phrasal verbs you'll use talking about cities...`,
      
      questions: [
        {
          type: "match",
          q: "Match phrasal verbs with their meanings:",
          pairs: [
            { left: "Pick up", right: "Recoger, levantar" },
            { left: "Show off", right: "Presumir, mostrar con orgullo" },
            { left: "Point out", right: "Señalar, indicar" },
            { left: "Find out", right: "Descubrir, averiguar" }
          ],
          a: "completed",
          successtip: "✓ ESSENTIAL VERBS!\nThese four = super common in daily English! Master them! 🎯"
        },
        {
          type: "mc",
          q: "'I love to ___ my city to visitors!' (show proudly)",
          options: [
            "show off",
            "show up",
            "show in"
          ],
          a: "show off",
          successtip: "✓ PROUD DISPLAY!\n'Show off' = mostrar con orgullo. Can be positive (city) or negative (bragging about self)! ✨"
        },
        {
          type: "input",
          q: "'Can you ___ ___ where the museum is?' (señalar)",
          a: "point out",
          accept: { ignoreCase: true, alts: ["point / out"] },
          successtip: "✓ INDICATE!\n'Point out' = señalar, indicate. Very useful for giving directions! 👉🗺️"
        },
        {
          type: "mc",
          q: "'Let me ___ the restaurant before we go there.' (probar)",
          options: [
            "try out",
            "try on",
            "try off"
          ],
          a: "try out",
          successtip: "✓ TEST IT!\n'Try out' = probar (test). 'Try on' = probarse (clothes). Different! 🍽️👕"
        },
        {
          type: "order",
          q: "Traduce: Necesito averiguar cuándo abre el museo (use 'find out')",
          a: "I need to find out when the museum opens",
          successtip: "✓ DISCOVER INFO!\n'Find out' = averiguar, discover information. Super common! 🔍"
        },
        {
          type: "mc",
          q: "'Please ___ your coat - it's warm inside.' (remove)",
          options: [
            "take off",
            "take on",
            "take up"
          ],
          a: "take off",
          successtip: "✓ REMOVE!\n'Take off' = quitar(se). Opposite: 'put on' = ponerse. Clothing verbs! 🧥"
        },
        {
          type: "input",
          q: "'Can you ___ ___ my bag while I buy tickets?' (hold temporarily)",
          a: "hold onto",
          accept: { ignoreCase: true, alts: ["hold / onto", "hold", "watch"] },
          successtip: "✓ TEMPORARY HOLD!\n'Hold onto' = sujetar. 'Watch' = vigilar. Both work for bags! 👜✋"
        },
        {
          type: "mc",
          q: "'The city wants to ___ the old buildings.' (renovar, arreglar)",
          options: [
            "fix up",
            "fix out",
            "fix away"
          ],
          a: "fix up",
          successtip: "✓ RENOVATE!\n'Fix up' = arreglar, renovate. Cities do this to improve old neighborhoods! 🔨🏚️→🏠"
        },
        {
          type: "order",
          q: "Traduce: Voy a buscar la dirección en mi teléfono (use 'look up')",
          a: "I am going to look up the address on my phone",
          successtip: "✓ SEARCH INFO!\n'Look up' = buscar información (dictionary, phone, internet). Different from 'look for' (object)! 📱🔍"
        },
        {
          type: "mc",
          q: "'This paint will ___ the neighborhood!' (alegrar, iluminar)",
          options: [
            "brighten up",
            "bright up",
            "light up"
          ],
          a: "brighten up",
          successtip: "✓ ADD COLOR!\n'Brighten up' = alegrar, make brighter. Murals and colors improve dull neighborhoods! 🎨✨"
        }
      ]
    },

    // ========================================================================
    // SET 6: THE PRONOUN RULE - CRITICAL! ‼️
    // ========================================================================
    {
      id: "m7-u7-s6",
      title: "Set 6: PRONOUN RULE - Must Go in Middle! ‼️",
      context: `**Giving Directions with Objects**

Friend: "I see trash on the ground. What should I do?"

You: "Pick it up! Don't leave it there!"`,
      
      questions: [
        {
          type: "mc",
          q: "'Pick up it' or 'Pick it up'?",
          options: [
            "Pick it up (pronoun MUST go in middle!)",
            "Pick up it (this is wrong!)",
            "Both are correct"
          ],
          a: "Pick it up (pronoun MUST go in middle!)",
          successtip: "✓ CRITICAL RULE!\nWith pronouns (it, them, him, her), ONLY middle position works! This is THE rule! ⚠️✅"
        },
        {
          type: "input",
          q: "'There's a paper. Pick ___ ___.' (it + up)",
          a: "it up",
          accept: { ignoreCase: true, alts: ["it / up"] },
          successtip: "✓ PRONOUN IN MIDDLE!\n'Pick IT up' ✅. Never 'Pick up it' ❌. Super common error! 📄"
        },
        {
          type: "mc",
          q: "Friend: 'The lights are off!' You: '___' (turn + them + on)",
          options: [
            "Turn them on",
            "Turn on them",
            "Both work"
          ],
          a: "Turn them on",
          successtip: "✓ THEM = PRONOUN!\n'Turn THEM on' ✅. 'Turn on them' ❌ = WRONG! Pronoun must go in middle! 💡"
        },
        {
          type: "order",
          q: "Fix the error: 'Throw away it' → 'Throw ___'",
          a: "it away",
          successtip: "✓ ERROR CORRECTED!\n'Throw IT away' = correct! Pronoun 'it' must be in the middle! 🗑️✅"
        },
        {
          type: "mc",
          q: "'I found your keys. Here, take ___.' (them + off the table)",
          options: [
            "them (just 'them', no phrasal verb needed here)",
            "off them",
            "them off"
          ],
          a: "them (just 'them', no phrasal verb needed here)",
          successtip: "✓ CONTEXT MATTERS!\nIn this case, just 'take them' works! Not all 'take' uses need particles! 🔑"
        },
        {
          type: "input",
          q: "'The form needs your signature. Please fill ___ ___.' (it + out)",
          a: "it out",
          accept: { ignoreCase: true, alts: ["it / out"] },
          successtip: "✓ PAPERWORK!\n'Fill IT out' ✅. Never 'fill out it' ❌. Pronoun rule applies to ALL separable verbs! 📝"
        },
        {
          type: "mc",
          q: "Why must pronouns go in the middle?",
          options: [
            "Grammatical rule - pronouns are 'light' and attach to the verb",
            "Just tradition, no real reason",
            "They don't - both positions work"
          ],
          a: "Grammatical rule - pronouns are 'light' and attach to the verb",
          successtip: "✓ LINGUISTIC REASON!\nPronouns = unstressed, 'light' words. They need to be close to the verb! Grammar rule! 📚"
        },
        {
          type: "order",
          q: "Traduce: Recógelo antes de que alguien se tropiece (pick up + it)",
          a: "Pick it up before someone trips",
          successtip: "✓ PRONOUN POSITION!\n'Pick IT up' = only correct way! Safety first! 🚨✅"
        },
        {
          type: "mc",
          q: "'She's showing her friends the city.' = 'She's showing ___ ___.'",
          options: [
            "it off",
            "off it",
            "them off"
          ],
          a: "it off",
          successtip: "✓ THE CITY = IT!\n'Showing IT off' (city = singular = it). Pronoun in middle! ✨🏙️"
        },
        {
          type: "mc",
          q: "CRITICAL: Which is the MOST IMPORTANT rule?",
          options: [
            "Pronouns (it, them, him, her) MUST go between verb and particle",
            "Pronouns can go anywhere",
            "Only 'it' follows this rule"
          ],
          a: "Pronouns (it, them, him, her) MUST go between verb and particle",
          successtip: "✓ RULE MASTERED!\nALL pronouns (it, them, him, her, me, us, you) = middle position! No exceptions! 💯⚠️"
        }
      ]
    },

    // ========================================================================
    // SET 7: INTENSIVE PRONOUN PRACTICE 💪
    // ========================================================================
    {
      id: "m7-u7-s7",
      title: "Set 7: Pronoun Practice - All Pronouns 💪",
      context: `**Real Conversations**

Practice with all pronouns: it, them, him, her, us...`,
      
      questions: [
        {
          type: "mc",
          q: "'The trash is there. Can you pick ___ ___?'",
          options: [
            "it up",
            "up it",
            "Both work"
          ],
          a: "it up",
          successtip: "✓ IT = PRONOUN!\n'Pick IT up' = only way! Review: nouns = flexible, pronouns = middle only! ✅"
        },
        {
          type: "input",
          q: "'The tourists are lost. Can you help ___ ___?' (them + out)",
          a: "them out",
          accept: { ignoreCase: true, alts: ["them / out"] },
          successtip: "✓ HELP THEM!\n'Help THEM out' = assist them. Pronoun in middle! 👥💙"
        },
        {
          type: "mc",
          q: "'My coat is dirty. I need to clean ___.' (it + up/off)",
          options: [
            "it (or 'it up' / 'it off')",
            "up it",
            "off it"
          ],
          a: "it (or 'it up' / 'it off')",
          successtip: "✓ CLEAN IT!\n'Clean it' or 'clean it off' = both work! Pronoun stays in middle if using particle! 🧥🧹"
        },
        {
          type: "order",
          q: "Traduce: Enciéndelas - está oscuro aquí (turn on + them = lights)",
          a: "Turn them on - it's dark here",
          successtip: "✓ LIGHTS ON!\n'Turn THEM on' = correct! 'Them' = pronoun for lights! 💡✅"
        },
        {
          type: "mc",
          q: "'I can't find the address. Can you look ___ ___?' (it + up)",
          options: [
            "it up",
            "up it",
            "Both work"
          ],
          a: "it up",
          successtip: "✓ SEARCH FOR IT!\n'Look IT up' = buscar. Pronoun middle position! 📱🔍"
        },
        {
          type: "input",
          q: "'The building is old. They're going to fix ___ ___.' (it + up)",
          a: "it up",
          accept: { ignoreCase: true, alts: ["it / up"] },
          successtip: "✓ RENOVATION!\n'Fix IT up' = renovate it. Pronoun rule applies! 🏚️→🏠"
        },
        {
          type: "mc",
          q: "'Meet me at 5pm. Don't let ___ ___!' (me + down)",
          options: [
            "me down",
            "down me",
            "Both work"
          ],
          a: "me down",
          successtip: "✓ ME = PRONOUN TOO!\n'Let ME down' = disappoint me. ALL pronouns follow the rule! 🤝"
        },
        {
          type: "order",
          q: "Fix: 'Please throw away them' → 'Please throw ___'",
          a: "them away",
          successtip: "✓ COMMON ERROR FIXED!\n'Throw THEM away' = correct! This error happens even at advanced levels! 🗑️"
        },
        {
          type: "mc",
          q: "'The city tour was cancelled. They called ___ ___.' (it + off)",
          options: [
            "it off",
            "off it",
            "Both work"
          ],
          a: "it off",
          successtip: "✓ CANCELLATION!\n'Call IT off' = cancel it. Pronoun in middle! Common phrasal verb! ❌📅"
        },
        {
          type: "mc",
          q: "Summary: ALL pronouns (it, them, him, her, me, us, you) must:",
          options: [
            "Go between the verb and particle (middle position)",
            "Go after the particle",
            "Can go anywhere"
          ],
          a: "Go between the verb and particle (middle position)",
          successtip: "✓ UNIVERSAL RULE!\nALL pronouns = middle! No exceptions! This is one of English's fixed rules! 💯✅"
        }
      ]
    },

    // ========================================================================
    // SET 8: CLEANING UP THE CITY 🧹🏙️
    // ========================================================================
    {
      id: "m7-u7-s8",
      title: "Set 8: City Improvement 🧹🏙️",
      context: `**Community Initiative**

Mayor: "Our city needs improvement. Here's our plan..."`,
      
      questions: [
        {
          type: "mc",
          q: "Mayor: 'Volunteers will ___ litter from the parks.'",
          options: [
            "pick up",
            "pick out",
            "pick off"
          ],
          a: "pick up",
          successtip: "✓ COMMUNITY CLEANUP!\n'Pick up litter' = community service! Keeps cities clean! 🧹🌳"
        },
        {
          type: "input",
          q: "Mayor: 'We need to ___ ___ the streets every week.' (clean + up)",
          a: "clean up",
          accept: { ignoreCase: true, alts: ["clean / up"] },
          successtip: "✓ STREET CLEANING!\n'Clean up streets' = important city service! Regular maintenance! 🧹🛣️"
        },
        {
          type: "mc",
          q: "Volunteer: 'There's so much trash! Should I throw ___ ___?' (it + away)",
          options: [
            "it away",
            "away it",
            "Both work"
          ],
          a: "it away",
          successtip: "✓ PRONOUN RULE!\n'Throw IT away' = correct! Trash = it (pronoun) = middle position! 🗑️✅"
        },
        {
          type: "order",
          q: "Traduce: Vamos a arreglar los edificios viejos del centro (fix up)",
          a: "We are going to fix up the old buildings downtown",
          successtip: "✓ URBAN RENEWAL!\n'Fix up buildings' = renovate them. Urban improvement! 🏚️🔨"
        },
        {
          type: "mc",
          q: "Mayor: 'New murals will ___ the neighborhoods.' (alegrar)",
          options: [
            "brighten up",
            "bright up",
            "light up"
          ],
          a: "brighten up",
          successtip: "✓ ADD COLOR!\n'Brighten up' = add life and color! Art improves communities! 🎨✨"
        },
        {
          type: "input",
          q: "Reporter: 'Will you ___ ___ new bike lanes?' (install = put + in)",
          a: "put in",
          accept: { ignoreCase: true, alts: ["put / in", "install"] },
          successtip: "✓ INFRASTRUCTURE!\n'Put in' = install. Bike lanes = modern city planning! 🚴‍♂️🛣️"
        },
        {
          type: "mc",
          q: "Mayor: 'We'll ___ free public WiFi across the city.' (establecer)",
          options: [
            "set up",
            "set off",
            "set out"
          ],
          a: "set up",
          successtip: "✓ ESTABLISH!\n'Set up' = establecer, create. Free WiFi = modern city amenity! 📶🏙️"
        },
        {
          type: "order",
          q: "Rewrite with pronoun: 'Clean up the park' → 'Clean ___ ___' (it)",
          a: "it up",
          successtip: "✓ PRONOUN SWITCH!\nPark = it. 'Clean IT up' = pronoun goes in middle! ✅"
        },
        {
          type: "mc",
          q: "Citizen: 'The old factory is dangerous. You should tear ___ ___.' (it + down)",
          options: [
            "it down",
            "down it",
            "Both work"
          ],
          a: "it down",
          successtip: "✓ DEMOLISH!\n'Tear IT down' = demolish. Factory = it (pronoun) = middle! 🏭💥"
        },
        {
          type: "mc",
          q: "Mayor: 'These changes will ___ our city significantly!' (improve = make better)",
          options: [
            "improve (not always a phrasal verb!)",
            "improve up",
            "improve off"
          ],
          a: "improve (not always a phrasal verb!)",
          successtip: "✓ SIMPLE VERB!\n'Improve' = simple verb, no particle needed! Not everything is phrasal! 📈✨"
        }
      ]
    },

    // ========================================================================
    // SET 9: CITY TOUR - SHOWING OFF ✨
    // ========================================================================
    {
      id: "m7-u7-s9",
      title: "Set 9: Tour Guide - Showing Off the City ✨",
      context: `**Professional Tour Guide**

Guide: "Welcome to our beautiful city! Let me show you around..."`,
      
      questions: [
        {
          type: "mc",
          q: "Guide: 'I love to ___ our city to visitors!' (mostrar con orgullo)",
          options: [
            "show off",
            "show up",
            "show in"
          ],
          a: "show off",
          successtip: "✓ PROUD DISPLAY!\n'Show off' = display proudly! Tour guides love showing off their cities! ✨🏙️"
        },
        {
          type: "input",
          q: "Tourist: 'Can you ___ ___ the best restaurants?' (señalar, indicate)",
          a: "point out",
          accept: { ignoreCase: true, alts: ["point / out", "show"] },
          successtip: "✓ INDICATE!\n'Point out' = señalar, show. Essential for tour guides! 👉🍽️"
        },
        {
          type: "mc",
          q: "Guide: 'Let me ___ some interesting facts about this building.' (señalar)",
          options: [
            "point out",
            "point in",
            "point up"
          ],
          a: "point out",
          successtip: "✓ SHARE FACTS!\n'Point out facts' = share interesting information! Educational! 🏛️📖"
        },
        {
          type: "order",
          q: "Traduce: Averigüemos cuándo abre el museo (find out + when)",
          a: "Let's find out when the museum opens",
          successtip: "✓ DISCOVER INFO!\n'Find out' = averiguar! Useful for planning! 🔍🏛️"
        },
        {
          type: "mc",
          q: "Tourist: 'Is that restaurant good?' Guide: 'Yes! You should ___ ___ tonight!' (probar)",
          options: [
            "try it out",
            "try out it",
            "try it on"
          ],
          a: "try it out",
          successtip: "✓ TEST IT!\n'Try IT out' = test it. Restaurant = it (pronoun) = middle position! 🍽️✅"
        },
        {
          type: "input",
          q: "Guide: 'I'll ___ ___ where to buy tickets.' (descubrir, averiguar)",
          a: "find out",
          accept: { ignoreCase: true, alts: ["find / out"] },
          successtip: "✓ GET INFO!\n'Find out' = discover information. Super common! 🎫🔍"
        },
        {
          type: "mc",
          q: "Tourist: 'Can we visit the tower?' Guide: 'Let me ___ ___ if it's open.' (check)",
          options: [
            "find out",
            "find in",
            "find up"
          ],
          a: "find out",
          successtip: "✓ CHECK AVAILABILITY!\n'Find out if...' = check whether something is true! 🗼❓"
        },
        {
          type: "order",
          q: "Fix with pronoun: 'Let me show off the cathedral' → 'Let me show ___ ___'",
          a: "it off",
          successtip: "✓ PRONOUN FORM!\nCathedral = it. 'Show IT off' = pronoun in middle! ⛪✅"
        },
        {
          type: "mc",
          q: "Guide: 'That building was ___ last year - now it's a museum.' (fixed = renovated)",
          options: [
            "fixed up",
            "fixed out",
            "fixed off"
          ],
          a: "fixed up",
          successtip: "✓ RENOVATION!\n'Fixed up' = renovated. Old buildings become museums! 🏛️✨"
        },
        {
          type: "mc",
          q: "Tourist: 'This tour is amazing! You really ___ your city!' (know well = understand fully)",
          options: [
            "know (simple verb, no particle needed)",
            "know off",
            "know up"
          ],
          a: "know (simple verb, no particle needed)",
          successtip: "✓ EXPERTISE!\n'Know your city' = simple verb! Not all verbs need particles! Tour guide = expert! 🌟"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE MASTERY 🌟
    // ========================================================================
    {
      id: "m7-u7-s10",
      title: "Set 10: City Life Complete Mastery 🌟",
      context: `**FINAL CHALLENGE**

You're writing about your experience moving to a new vibrant city. Use all vocabulary and phrasal verbs correctly!`,
      
      questions: [
        {
          type: "mc",
          q: "Blog: 'I moved to a ___ city last year - there's always energy and activity!'",
          options: [
            "vibrant / bustling",
            "dead",
            "boring"
          ],
          a: "vibrant / bustling",
          successtip: "✓ ENERGETIC CITY!\n'Vibrant' or 'bustling' = full of life! Perfect city adjectives! 🏙️✨"
        },
        {
          type: "input",
          q: "Blog: 'The downtown has amazing ___ - the tallest is 80 floors!' (tall buildings)",
          a: "skyscrapers",
          accept: { ignoreCase: true, alts: ["buildings", "towers"] },
          successtip: "✓ TALL STRUCTURES!\n'Skyscrapers' = perfect word! Shows modern city! 🏢☁️"
        },
        {
          type: "mc",
          q: "Blog: 'I love to ___ my city to visiting friends!' (mostrar con orgullo)",
          options: [
            "show off",
            "show up",
            "show in"
          ],
          a: "show off",
          successtip: "✓ CITY PRIDE!\n'Show off' = display proudly! You love your new city! 💚✨"
        },
        {
          type: "order",
          q: "Traduce: La galería de arte está cerca de la estación del metro",
          a: "The art gallery is near the metro station",
          successtip: "✓ CULTURAL LOCATION!\nArt gallery + metro = perfect combination! City vocabulary! 🎨🚇"
        },
        {
          type: "mc",
          q: "Blog: 'On weekends, I help ___ litter from the park.'",
          options: [
            "pick up",
            "pick out",
            "pick off"
          ],
          a: "pick up",
          successtip: "✓ COMMUNITY SERVICE!\n'Pick up litter' = help clean! Good citizen! 🧹🌳💚"
        },
        {
          type: "mc",
          q: "Blog: 'There was trash everywhere, so I picked ___ ___.' (pronoun!)",
          options: [
            "it up",
            "up it",
            "Both work"
          ],
          a: "it up",
          successtip: "✓ PRONOUN RULE APPLIED!\n'Picked IT up' = correct! Trash = it (pronoun) = middle! ✅🗑️"
        },
        {
          type: "input",
          q: "Blog: 'The neighborhood was run-down, but they're ___ it ___ now.' (fix + it + up)",
          a: "fixing, up",
          accept: { ignoreCase: true, alts: ["fixing / up", "fixing it up"] },
          successtip: "✓ URBAN RENEWAL!\n'Fixing IT up' = renovating! Pronoun in middle! 🏚️→🏠✨"
        },
        {
          type: "mc",
          q: "Blog: 'The city is quite ___ during rush hour - too many cars!' (noisy? crowded?)",
          options: [
            "crowded / noisy (both work!)",
            "empty",
            "peaceful"
          ],
          a: "crowded / noisy (both work!)",
          successtip: "✓ RUSH HOUR!\nBoth adjectives = perfect for describing busy traffic! Real city life! 🚗🚙😰"
        },
        {
          type: "order",
          q: "Traduce: Necesito averiguar dónde está la farmacia más cercana (find out + chemist's UK)",
          a: "I need to find out where the nearest chemist's is",
          successtip: "✓ UK VOCABULARY!\n'Find out' + 'chemist's' (UK) = both learned! Could also say 'pharmacy'! 💊🔍🇬🇧"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which sentence shows COMPLETE Unit 7 mastery?",
          options: [
            "'I moved to a vibrant city with skyscrapers and art galleries near the metro station. On weekends, I pick up litter and help clean it up. I love to show off the city - visitors should try it out!'",
            "'I moved to dead city with buildings near station. On weekend, I pick it up litter and help clean up it. I love show the city - visitors should try out it!'",
            "'I moving vibrant city with skyscrapers. I picking up litter and cleaning. I showing city to visitors!'"
          ],
          a: "'I moved to a vibrant city with skyscrapers and art galleries near the metro station. On weekends, I pick up litter and help clean it up. I love to show off the city - visitors should try it out!'",
          successtip: "🎉 UNIT 7 COMPLETELY MASTERED! 🎉\n\n✅ CITY VOCABULARY\n• Adjectives: vibrant, bustling, polluted, modern ✅\n• Landmarks: skyscraper, art gallery, monument, bridge ✅\n• UK-US: chemist's/pharmacy, pavement/sidewalk, metro/subway ✅\n\n✅ SEPARABLE PHRASAL VERBS\n• Pick up, clean up, throw away, show off ✅\n• Point out, find out, try out, fix up ✅\n• Turn on/off, brighten up, set up ✅\n\n✅ NOUN RULE\n• Flexible position: 'pick up litter' = 'pick litter up' ✅\n\n✅ PRONOUN RULE (CRITICAL!)\n• MUST go in middle: 'pick IT up' ✅\n• NEVER after particle: 'pick up it' ❌\n• All pronouns (it, them, him, her, me, us) ✅\n\n✅ CORRECT IN SENTENCE:\n• 'Clean IT up' (pronoun middle) ✅\n• 'Try IT out' (pronoun middle) ✅\n• All phrasal verbs used naturally ✅\n\n🌟 YOU CAN NOW:\n• Describe any city completely ✅\n• Use separable phrasal verbs naturally ✅\n• Apply pronoun rule automatically ✅\n• Navigate UK-US urban vocabulary ✅\n• Sound like a native speaker! ✅\n\nB1-B2 cities & phrasal verbs = MASTERED! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - A DAY EXPLORING THE CITY (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u7-reading",
    title: "Reading Practice - A Day Exploring the City",
    category: "reading",
    
    questions: [
    {
        type: "passage",
        passage: `**A Day Exploring the City**

**Morning - 7:00 AM**

I wake up in my new vibrant city. From my apartment window, I can see skyscrapers and a bustling street below. Today I'll show off my city to my friend visiting from abroad. First, let me find out which museums are open.`
      },
      {
        type: "mc",
        q: "What city adjective is used?",
        options: [
          "Vibrant",
          "Polluted",
          "Boring"
        ],
        a: "Vibrant",
        successtip: "✓ CITY DESCRIPTION!\n'Vibrant' = full of energy! Perfect positive adjective for cities! 🏙️✨"
      },
      {
        type: "input",
        q: "'Find out' means:",
        a: "discover",
        accept: { ignoreCase: true, alts: ["discover", "learn", "check"] },
        successtip: "✓ PHRASAL VERB!\n'Find out' = descubrir, averiguar. Checking museum hours! 🔍"
      },

      // ======================================================================
      // BLOQUE 2: Exploring - Visiting Landmarks (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Late Morning - City Tour**

We're exploring downtown. I'm pointing out famous landmarks - the old cathedral, the modern art gallery, and the historic monument in the main square. My friend loves it! I turn on my phone to look up the nearest café.`
      },
      {
        type: "mc",
        q: "'Pointing out' means:",
        options: [
          "Showing/indicating",
          "Drawing",
          "Hiding"
        ],
        a: "Showing/indicating",
        successtip: "✓ SHOWING LANDMARKS!\n'Point out' = señalar, indicate. Tour guide essential! 👉🏛️"
      },
      {
        type: "mc",
        q: "Which phrasal verbs appear in this passage?",
        options: [
          "Three: pointing out, turn on, look up",
          "None",
          "Only one"
        ],
        a: "Three: pointing out, turn on, look up",
        successtip: "✓ PHRASAL VERB COUNT!\nThree separable phrasal verbs used naturally! Good spotting! ✅"
      },

      // ======================================================================
      // BLOQUE 3: Problems - Seeing Issues (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Afternoon - City Center**

The city center is crowded and noisy. Unfortunately, I notice the streets are quite polluted - there's litter everywhere. Someone should pick it up! The government is trying to clean up the area, but it takes time. Still beautiful though.`
      },
      {
        type: "mc",
        q: "'Pick it up' - why is 'it' in the middle?",
        options: [
          "Because 'it' is a pronoun - must go in middle",
          "Just random placement",
          "Could also say 'pick up it'"
        ],
        a: "Because 'it' is a pronoun - must go in middle",
        successtip: "✓ PRONOUN RULE!\n'Pick IT up' = only correct way! Pronouns MUST go in middle! Critical rule! ⚠️✅"
      },
      {
        type: "input",
        q: "Two negative adjectives used:",
        a: "crowded, noisy",
        accept: { ignoreCase: true, alts: ["crowded / noisy", "crowded and noisy", "polluted"] },
        successtip: "✓ NEGATIVE DESCRIPTIONS!\n'Crowded', 'noisy', 'polluted' = all negative city adjectives in passage! 🏙️😰"
      },

      // ======================================================================
      // BLOQUE 4: Solutions - Community Cleanup (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Late Afternoon - Park**

We join a community cleanup event in the park. Volunteers are picking up trash and cleaning up the green spaces. We throw it away in proper bins. The city plans to brighten up this area with murals. Community spirit!`
      },
      {
        type: "mc",
        q: "How many separable phrasal verbs are used?",
        options: [
          "Four: picking up, cleaning up, throw away, brighten up",
          "Two",
          "None"
        ],
        a: "Four: picking up, cleaning up, throw away, brighten up",
        successtip: "✓ CLEANUP VERBS!\nAll four = action verbs for community improvement! Great vocabulary! 🧹✨"
      },
      {
        type: "mc",
        q: "'We throw it away' - is the pronoun position correct?",
        options: [
          "Yes - 'it' is in the middle (throw IT away)",
          "No - should be 'throw away it'",
          "Both positions work with pronouns"
        ],
        a: "Yes - 'it' is in the middle (throw IT away)",
        successtip: "✓ CORRECT PRONOUN!\n'Throw IT away' = perfect! Pronoun in middle! Rule applied correctly! ✅🗑️"
      },

      // ======================================================================
      // BLOQUE 5: Evening - Showing Off + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Evening - City Lights**

As evening arrives, I show off the city's illuminated skyscrapers to my friend. The lights turn on, and everything looks magical. Despite some problems, this diverse city is home. Tomorrow we'll try out that new restaurant everyone's talking about!`
      },
      {
        type: "mc",
        q: "'Turn on' - could this also be written as 'turn them on'?",
        options: [
          "Yes - if referring to specific lights, 'turn them on' (pronoun in middle)",
          "No - 'turn on them' only",
          "Pronouns can't be used here"
        ],
        a: "Yes - if referring to specific lights, 'turn them on' (pronoun in middle)",
        successtip: "✓ PRONOUN OPTION!\n'Turn THEM on' = if lights = 'them'. Pronoun in middle! ✅💡"
      },
      {
        type: "mc",
        q: "Which positive city adjective appears in this passage?",
        options: [
          "Diverse",
          "Polluted",
          "Crowded"
        ],
        a: "Diverse",
        successtip: "✓ POSITIVE TRAIT!\n'Diverse' = multicultural, varied. Positive city quality! 🌍💚"
      },
      {
        type: "mc",
        q: "Throughout this reading, what grammar pattern appeared repeatedly?",
        options: [
          "Separable phrasal verbs with correct pronoun placement",
          "Past tense only",
          "No special patterns"
        ],
        a: "Separable phrasal verbs with correct pronoun placement",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ CITY VOCABULARY\n• Adjectives: vibrant, bustling, crowded, noisy, polluted, diverse ✅\n• Landmarks: skyscrapers, cathedral, art gallery, monument, square ✅\n\n✅ SEPARABLE PHRASAL VERBS\n• Find out, point out, pick up, clean up ✅\n• Throw away, turn on, look up, show off ✅\n• Brighten up, try out ✅\n\n✅ PRONOUN RULE (Applied Correctly!)\n• 'Pick IT up' (not 'pick up it') ✅\n• 'Throw IT away' (not 'throw away it') ✅\n• 'Turn them on' (not 'turn on them') ✅\n\n✅ NATURAL USAGE\n• All phrasal verbs used in real contexts ✅\n• Pronouns positioned correctly every time ✅\n• City life described naturally ✅\n\n🌟 UNIT 7 IN ACTION!\nCity vocabulary + Phrasal verbs + Pronoun rule = Natural English! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 7 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u7-review",
    title: "⭐ Unit 7 Review: Cities & Phrasal Verbs",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which is CORRECT?",
        options: [
          "Pick it up (pronoun in middle)",
          "Pick up it (pronoun after particle)",
          "Both are correct"
        ],
        a: "Pick it up (pronoun in middle)",
        successtip: "✓ CRITICAL RULE!\nPronouns MUST go in middle! 'Pick IT up' ✅. 'Pick up it' ❌ = WRONG! ⚠️"
      },
      
      {
        type: "input",
        q: "UK: 'chemist's' = US: '___'",
        a: "pharmacy",
        accept: { ignoreCase: true, alts: ["drugstore"] },
        successtip: "✓ MEDICINE SHOP!\nUK: 'chemist's'. US: 'pharmacy' or 'drugstore'. Same place! 💊🇬🇧🇺🇸"
      },
      
      {
        type: "mc",
        q: "A city full of life and energy is:",
        options: [
          "Vibrant / Lively",
          "Dead / Boring",
          "Empty"
        ],
        a: "Vibrant / Lively",
        successtip: "✓ POSITIVE ADJECTIVES!\n'Vibrant' or 'lively' = full of energy! Great city descriptions! ✨🏙️"
      },
      
      {
        type: "order",
        q: "Fix the error: 'Throw away them' → 'Throw ___'",
        a: "them away",
        successtip: "✓ PRONOUN CORRECTION!\n'Throw THEM away' = correct! Pronoun must be in middle! ✅"
      },
      
      {
        type: "mc",
        q: "With NOUN objects, separable phrasal verbs are:",
        options: [
          "Flexible - can go before or after particle",
          "Fixed - only one position",
          "Not allowed"
        ],
        a: "Flexible - can go before or after particle",
        successtip: "✓ NOUN FLEXIBILITY!\n'Pick up litter' = 'Pick litter up'. Both OK with nouns! Different from pronouns! 📦✅"
      },
      
      {
        type: "input",
        q: "'___' means to show something proudly (phrasal verb with 'off')",
        a: "show off",
        accept: { ignoreCase: true, alts: ["show / off"] },
        successtip: "✓ DISPLAY PROUDLY!\n'Show off' = mostrar con orgullo! Great for showing your city! ✨"
      },
      
      {
        type: "mc",
        q: "Very tall modern buildings are called:",
        options: [
          "Skyscrapers",
          "Monuments",
          "Cathedrals"
        ],
        a: "Skyscrapers",
        successtip: "✓ TALL BUILDINGS!\n'Skyscrapers' = rascacielos! Iconic feature of modern cities! 🏢☁️"
      },
      
      {
        type: "order",
        q: "Traduce: Necesito averiguar cuándo abre el museo (find out)",
        a: "I need to find out when the museum opens",
        successtip: "✓ DISCOVER INFO!\n'Find out' = averiguar! Super common phrasal verb! 🔍🏛️"
      },
      
      {
        type: "mc",
        q: "UK: 'Underground' or 'Tube' = US: '___'",
        options: [
          "Subway",
          "Train",
          "Bus"
        ],
        a: "Subway",
        successtip: "✓ METRO SYSTEM!\nUK: 'Underground/Tube'. US: 'Subway'. Europe: often 'Metro'. Same transport! 🚇🌍"
      },
      
      {
        type: "mc",
        q: "FINAL QUESTION: What is the MOST CRITICAL rule about separable phrasal verbs?",
        options: [
          "Pronouns (it, them, him, her) MUST go in the middle - NEVER after the particle",
          "Nouns must always go after the particle",
          "There are no rules - any position works"
        ],
        a: "Pronouns (it, them, him, her) MUST go in the middle - NEVER after the particle",
        successtip: "🎉 UNIT 7 COMPLETELY MASTERED! 🎉\n\n✅ CITY VOCABULARY\n• Adjectives: vibrant, bustling, crowded, noisy, polluted, diverse, modern, historic ✅\n• Landmarks: skyscraper, art gallery, museum, monument, cathedral, bridge, square ✅\n• UK-US differences: chemist's/pharmacy, pavement/sidewalk, underground/subway ✅\n\n✅ SEPARABLE PHRASAL VERBS\n• Pick up (recoger) ✅\n• Clean up (limpiar) ✅\n• Throw away (tirar) ✅\n• Show off (presumir/mostrar) ✅\n• Point out (señalar) ✅\n• Find out (averiguar) ✅\n• Try out (probar) ✅\n• Turn on/off (encender/apagar) ✅\n• Fix up (arreglar) ✅\n• Brighten up (alegrar) ✅\n\n✅ NOUN RULE\n• Flexible position ✅\n• 'Pick up the litter' = 'Pick the litter up' ✅\n• Both are correct! ✅\n\n✅ PRONOUN RULE (CRITICAL!) ⚠️\n• MUST go in middle ✅\n• 'Pick IT up' ✅ (CORRECT!)\n• 'Pick up IT' ❌ (WRONG!)\n• ALL pronouns: it, them, him, her, me, us, you ✅\n• NO EXCEPTIONS! ✅\n\n✅ REAL APPLICATIONS\n• Describing cities ✅\n• Community cleanup ✅\n• Tour guiding ✅\n• Daily city activities ✅\n\n🌟 YOU CAN NOW:\n• Describe any city with rich vocabulary ✅\n• Use separable phrasal verbs naturally ✅\n• Apply the pronoun rule automatically ✅\n• Avoid the #1 phrasal verb error ✅\n• Navigate UK-US urban vocabulary ✅\n\nB1-B2 cities & separable phrasal verbs = MASTERED! 💪\n\n¡Excelente trabajo! Ready for Unit 8! 🚀"
      }
    ]
  }
};
